import React, { useState, useEffect } from 'react';
import useNewsRefresh from '../../../hooks/useNewsRefresh';
import './NewsFeed.css';

const NewsFeed = ({ symbol }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get backend URL for API calls
  const getBackendUrl = () => {
    // First, try React environment variables
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    
    // Second, try regular environment variables
    if (process.env.BACKEND_URL) {
      return process.env.BACKEND_URL;
    }
    
    // Third, check if we're running on Firebase Hosting and use Cloud Run backend
    if (typeof window !== 'undefined' && window.location.hostname) {
      const hostname = window.location.hostname;
      
      if (hostname.includes('firebaseapp.com') || 
          hostname.includes('web.app') ||
          hostname.includes('viraltrade.in') ||
          hostname.includes('viraltrade-19032')) {
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }
      
      // Fourth, check if we're running on Cloud Run and use hardcoded URL
      if (hostname.includes('run.app')) {
        return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
      }
    }
    
    // Finally, fallback to localhost for development
    return 'http://localhost:8000';
  };

  const BASE_URL = getBackendUrl();

  const fetchNews = async () => {
    setLoading(true);
    
    try {
      // Fetch news from backend API (calls Finnhub API)
      const response = await fetch(`${BASE_URL}/api/news/trading`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.data) {
        // Transform Finnhub API response to match our component structure
        const transformedNews = data.data.map((item, index) => ({
          id: item.id || index + 1,
          title: item.headline || 'No title available',
          summary: item.summary || 'No summary available',
          source: item.source || 'Unknown source',
          publishedAt: item.datetime ? new Date(item.datetime * 1000).toISOString() : new Date().toISOString(),
          url: item.url || '#',
          sentiment: 'neutral' // Finnhub doesn't provide sentiment, default to neutral
        }));
        
        setNews(transformedNews);
      } else {
        setNews([]);
      }
    } catch (err) {
      console.error('Failed to fetch news:', err);
      setError(err.message);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  // Use the news refresh hook for automatic refresh on page load
  const { refreshNews } = useNewsRefresh(fetchNews, [], 'newsFeedLastRefreshTime');

  useEffect(() => {
    // Initial fetch on component mount
    fetchNews();
    
    // Check if we should refresh on page load (if more than 1 minute has passed since last refresh)
    const storedTime = localStorage.getItem('newsFeedLastRefreshTime');
    if (storedTime) {
      const lastRefresh = new Date(storedTime).getTime();
      const now = new Date().getTime();
      const timeDiff = now - lastRefresh;
      const oneMinute = 60 * 1000;
      
      if (timeDiff >= oneMinute) {
        console.log('Page refreshed - fetching latest news feed');
        refreshNews();
      } else {
        console.log('Page refreshed - using cached news feed (refresh cooldown active)');
      }
    } else {
      // First time loading - fetch news
      console.log('First time loading - fetching news feed');
    }
  }, []); // Only run on mount

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const getSentimentIcon = (sentiment) => {
    switch (sentiment) {
      case 'positive': return '📈';
      case 'negative': return '📉';
      default: return '📊';
    }
  };

  if (loading) {
    return (
      <div className="news-feed">
        <div className="loading-placeholder">
          <div className="loading-spinner">⏳</div>
          <p>Loading news...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="news-feed">
        <div className="error-state">
          <div className="error-icon">❌</div>
          <p>Failed to load news</p>
          <p className="error-details">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!news.length) {
    return (
      <div className="news-feed">
        <div className="no-news">
          <p>No news available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="news-feed">

      
      <div className="news-list">
        {news.map((item) => (
          <div key={item.id} className="news-item">
            <div className="news-header">
              <span className="sentiment-icon">{getSentimentIcon(item.sentiment)}</span>
              <span className="news-source">{item.source}</span>
              <span className="news-time">{formatDate(item.publishedAt)}</span>
            </div>
            <h4 className="news-title">
              <a href={item.url} target="_blank" rel="noopener noreferrer">
                {item.title}
              </a>
            </h4>
            <p className="news-summary">{item.summary}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsFeed; 